<template>
  <div class="category-filter">
    <ul>
      <li
        v-for="item in mapped"
        :key="item.key"
        class="category-filter-item"
        :class="[
          'text-' + item.color,
          {
            'is-active': selected === item.key && item.facets.length,
          },
        ]"
      >
        <NuxtLink
          :to="item.to"
          :class="[
            'text-' + item.color,
            {
              'is-active': selected === item.key,
              'is-muted': selected && selected !== item.key,
            },
          ]"
          :disabled="item.count === 0 ? true : undefined"
        >
          <div>{{ item.label }}</div>
          <div class="category-filter-count">
            <span>{{ item.count }}</span>
          </div>
        </NuxtLink>
        <div
          v-if="item.facets.length && selected === item.key"
          class="category-filter-item-facets"
        >
          <SearchFacet
            v-for="facet in item.facets"
            :key="facet.key"
            :terms="facet.terms"
            :facet-key="facet.key"
            :label="facet.label"
          />
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
/*
import Vue from 'vue'
import { PropOptions } from 'vue/types'
import Facet from '~/features/search/components/Facet/index.vue'
import {
 CATEGORY_FACETS_MAP,
 CATEGORY_COLORS,
} from '~/features/search/settings'
import { filterRouteQuery } from '~/features/search/helpers'
import { SearchFacet, SearchFacetTerm } from '~/features/search/types'
import { getQueryParamString } from '~/helpers'

interface FilterPageCategoryTerm extends SearchFacetTerm {
 color: string
 to: any
 facets: SearchFacet[]
}
*/

import type {
  SearchFacet,
  SearchFacetTerm,
} from '~~/layers/legacy-search/app/types'
import {
  CATEGORY_FACETS_MAP,
  CATEGORY_COLORS,
} from '~~/layers/legacy-search/app/settings'
import { filterRouteQuery } from '~~/layers/legacy-search/app/helpers/search'
import { getQueryParamString } from '~/helpers'

const props = withDefaults(
  defineProps<{
    terms: SearchFacetTerm[]
    facets: SearchFacet[]
  }>(),
  {
    terms: () => [],
    facets: () => [],
  },
)

const route = useRoute()

function getColor(id: string) {
  return CATEGORY_COLORS[id] || ''
}

const CATEGORY_WEIGHT: Record<string, number> = {
  sightseeing: 100,
  food_drinks: 90,
  nightlife: 80,
  shopping: 70,
  culture: 60,
  recreation: 50,
}

function getCategoryFacets(id: string) {
  const facetIds = CATEGORY_FACETS_MAP[id] || []
  return props.facets.filter((v) => facetIds.includes(v.key))
}

const selected = computed(() => {
  /*
 selected(): string {
   return getQueryParamString(this.$route.query as any, 'category')
 }
 */
  return getQueryParamString(route.query as any, 'category')
})

function getLink(id: string) {
  const to = {
    name: route.name,
    query: {
      ...route.query,
    },
  }

  if (id !== selected.value) {
    to.query.category = id
  } else {
    delete to.query.category
  }

  delete to.query.page

  to.query = filterRouteQuery(to.query)

  return to
}

const { getFacetLabel } = await useSearchData()

const mapped = computed(() => {
  /*
 mapped(): FilterPageCategoryTerm[] {
   const terms = this.terms.map((v) => {
     const color = getColor(v.key)
     return {
       ...v,
       color,
       active: this.selected === v.key,
       label: v.label,
       to: this.getLink(v.key),
       facets: this.getCategoryFacets(v.key),
     }
   })

   return [...terms].sort((a, b) => {
     const wa = CATEGORY_WEIGHT[a.key] || 0
     const wb = CATEGORY_WEIGHT[b.key] || 0
     return wb - wa
   })
 }
 */
  const terms = props.terms.map((v) => {
    const color = getColor(v.key)
    return {
      ...v,
      color,
      active: selected.value === v.key,
      label: getFacetLabel(v.key),
      to: getLink(v.key),
      facets: getCategoryFacets(v.key),
    }
  })

  return [...terms].sort((a, b) => {
    const wa = CATEGORY_WEIGHT[a.key] || 0
    const wb = CATEGORY_WEIGHT[b.key] || 0
    return wb - wa
  })
})
</script>

<style lang="postcss">
.category-filter {
  @apply border-b border-solid border-border pb-size-7;
  margin-top: 0.5rem;
}

.category-filter-item {
  border-top: 2px solid transparent;
  border-bottom: 2px solid transparent;
  &.is-active {
    margin-bottom: 1rem;
    border-color: currentColor;
    &:first-child {
      border-top-color: transparent;
    }
    &:last-child {
      border-bottom-color: transparent;
      margin-bottom: 0;
    }
  }
  > a {
    @apply relative flex items-center justify-between text-size-4-5;
    padding: calc(1.25rem / 2) 2px;
    &.is-muted {
      @apply !text-muted;
      &:hover {
        @apply text-muted;
      }
    }
    &:not(.is-active) {
      &:before {
        @apply border-border bg-white;
      }
      .category-filter-count {
        @apply bg-scheme-main-ter;
        span {
          @apply text-muted-hover;
        }
      }
    }
    &[disabled] {
      @apply pointer-events-none !text-disabled;
      .category-filter-count {
        @apply bg-scheme-main-bis;
        span {
          @apply text-border-light;
        }
      }
    }
    div {
      @apply leading-[1.25rem];
    }
    div:first-child {
      @apply font-bold;
    }
    div:last-child {
      @apply ml-auto h-size-5 w-size-4 text-center text-size-7;
      background: currentColor;
      span {
        @apply text-white;
      }
    }
  }
}

.category-filter-item-facets {
  .facet:last-child {
    border-bottom: 0;
  }
}
</style>
